import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { patchProjectMetadata, updateElements } from '../../features/layout/layoutSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  DEFAULT_PROCESS_TYPE,
  DEFAULT_PROCESS_WAIT_SEC,
  equipmentProcessTypeLabel,
} from '../../features/equipment/equipmentConstants';
import { updateInstance } from '../../features/equipment/equipmentSlice';
import { selectExportableEquipmentInstanceMap } from '../../features/equipment/equipmentSelectors';
import type { EquipmentProcessType } from '../../features/equipment/equipmentTypes';
import { readStockerProcessTypes } from '../../features/equipment/stockerProcessTypes';
import {
  DEFAULT_PROCESS_FLOW_CYCLE_COUNT,
  DEFAULT_PROCESS_FLOW_TIME_SEC,
  PROCESS_FLOW_VERSION,
  createProcessFlowStepId,
  readProcessFlow,
  syncStepProcessTimes,
  toolsInStepOrder,
  type ProcessFlowStep,
} from '../../features/simulation/processFlow';
type Props = {
  onClose: () => void;
};

type ToolOption = {
  id: string;
  name: string;
  processType: EquipmentProcessType;
  processTimeSec: number;
};
const DEFAULT_STOCKER_WAIT_SEC = 30;

type StockerOption = {
  id: string;
  name: string;
  processTypes: EquipmentProcessType[];
  processWaitSec: number;
  source: 'equipment' | 'layout';
};

function readStockerWaitSec(value: unknown): number {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 0) return Math.round(parsed * 100) / 100;
  return DEFAULT_STOCKER_WAIT_SEC;
}

const ProcessFlowConfigModal: React.FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const projectMetadata = useAppSelector((state) => state.layout.projectMetadata);
  const equipmentInstances = useAppSelector(selectExportableEquipmentInstanceMap);
  const elements = useAppSelector((state) => state.layout.elements);
  const trackNodeAnnotations = useAppSelector((state) => state.layout.trackNodeAnnotations);

  const tools = useMemo<ToolOption[]>(
    () =>
      Object.values(equipmentInstances)
        .filter((instance) => instance.objectType === 'equipment')
        .map((instance) => ({
          id: instance.id,
          name: instance.name || instance.id,
          processType: instance.equipmentType ?? DEFAULT_PROCESS_TYPE,
          processTimeSec: instance.processWaitSec ?? DEFAULT_PROCESS_WAIT_SEC,
        })),
    [equipmentInstances]
  );

  const stockers = useMemo<StockerOption[]>(() => {
    const overlay = Object.values(equipmentInstances).filter(
      (instance) => instance.objectType === 'stocker'
    );
    const overlayIds = new Set(overlay.map((instance) => instance.id));
    const fromLayout = Object.values(elements).filter(
      (element) =>
        element.type === 'stocker' && element.metadata.visible && !overlayIds.has(element.id)
    );
    return [
      ...overlay.map((instance) => ({
        id: instance.id,
        name: instance.name || instance.id,
        source: 'equipment' as const,
        processWaitSec: readStockerWaitSec(instance.processWaitSec),
        processTypes: readStockerProcessTypes({
          processTypes: instance.processTypes,
          legacyProcessType: instance.equipmentType,
        }),
      })),
      ...fromLayout.map((element) => ({
        id: element.id,
        name: element.metadata.name || element.id,
        source: 'layout' as const,
        processWaitSec: readStockerWaitSec(element.customProperties?.processWaitSec),
        processTypes: readStockerProcessTypes({
          processTypes: element.customProperties?.processTypes,
          legacyProcessType:
            element.customProperties?.processType ?? element.customProperties?.toolType,
        }),
      })),
    ];
  }, [equipmentInstances, elements]);

  const toolsByProcessType = useMemo(() => {
    const grouped = new Map<string, ToolOption[]>();
    for (const tool of tools) {
      const group = grouped.get(tool.processType) ?? [];
      group.push(tool);
      grouped.set(tool.processType, group);
    }
    return grouped;
  }, [tools]);

  const hasEntryGate = trackNodeAnnotations.some((annotation) => annotation.role === 'entry');
  const hasExitGate = trackNodeAnnotations.some((annotation) => annotation.role === 'exit');

  const toolIdsFor = (processType: EquipmentProcessType): string[] =>
    (toolsByProcessType.get(processType) ?? []).map((tool) => tool.id);

  const defaultProcessTimesFor = (processType: EquipmentProcessType): Record<string, number> =>
    Object.fromEntries(
      (toolsByProcessType.get(processType) ?? []).map((tool) => [tool.id, tool.processTimeSec])
    );

  const defaultStockerIdsFor = (processType: EquipmentProcessType): string[] | null => {
    const matching = stockers.filter((stocker) => stocker.processTypes.includes(processType));
    const fallback = matching[0] ?? stockers[0];
    return fallback ? [fallback.id] : null;
  };

  const presentProcessTypes = useMemo(() => {
    const seen = new Set<EquipmentProcessType>();
    const ordered: EquipmentProcessType[] = [];
    for (const tool of tools) {
      if (seen.has(tool.processType)) continue;
      seen.add(tool.processType);
      ordered.push(tool.processType);
    }
    return ordered;
  }, [tools]);

  const [cycleCount] = useState(() => {
    const saved = readProcessFlow(projectMetadata).cycleCount;
    return saved > 0 ? saved : DEFAULT_PROCESS_FLOW_CYCLE_COUNT;
  });
  // Only process types with equipment on the layout appear as steps. Saved order
  // is kept for those that still have tools; new process types are appended.
  const [steps, setSteps] = useState<ProcessFlowStep[]>(() => {
    const presentSet = new Set(presentProcessTypes);

    const savedByProcessType = new Map<EquipmentProcessType, ProcessFlowStep>();
    for (const step of readProcessFlow(projectMetadata).steps) {
      if (!presentSet.has(step.processType)) continue;
      if (!savedByProcessType.has(step.processType)) {
        savedByProcessType.set(step.processType, step);
      }
    }

    const missing = presentProcessTypes
      .filter((processType) => !savedByProcessType.has(processType))
      .map((processType) => ({
        id: createProcessFlowStepId(),
        processType,
        stockerIds: defaultStockerIdsFor(processType),
        toolProcessTimes: {},
        toolActive: {},
        toolOrderIds: [],
      }));

    const claimedStockers = new Set<string>();
    return [...savedByProcessType.values(), ...missing].map((step) => {
      const stockerId =
        step.stockerIds?.find((id) => !claimedStockers.has(id)) ??
        stockers.find((stocker) => !claimedStockers.has(stocker.id))?.id ??
        null;
      if (stockerId) claimedStockers.add(stockerId);
      return syncStepProcessTimes(
        { ...step, stockerIds: stockerId ? [stockerId] : null },
        toolIdsFor(step.processType),
        defaultProcessTimesFor(step.processType)
      );
    });
  });
  const [stockerWaitSec, setStockerWaitSec] = useState<Record<string, number>>(() =>
    Object.fromEntries(stockers.map((stocker) => [stocker.id, stocker.processWaitSec]))
  );
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const updateStep = (id: string, patch: Partial<ProcessFlowStep>) => {
    setSteps((current) => current.map((step) => (step.id === id ? { ...step, ...patch } : step)));
  };

  const assignStocker = (stepId: string, stockerId: string | null) => {
    setSteps((current) =>
      current.map((step) => {
        if (step.id === stepId) {
          return { ...step, stockerIds: stockerId ? [stockerId] : null };
        }
        if (stockerId && step.stockerIds?.includes(stockerId)) {
          return { ...step, stockerIds: null };
        }
        return step;
      })
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps((current) => {
      const from = current.findIndex((step) => step.id === active.id);
      const to = current.findIndex((step) => step.id === over.id);
      if (from < 0 || to < 0) return current;
      return arrayMove(current, from, to);
    });
  };

  const handleToolDragEnd = (stepId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps((current) =>
      current.map((step) => {
        if (step.id !== stepId) return step;
        const from = step.toolOrderIds.indexOf(String(active.id));
        const to = step.toolOrderIds.indexOf(String(over.id));
        if (from < 0 || to < 0) return step;
        return { ...step, toolOrderIds: arrayMove(step.toolOrderIds, from, to) };
      })
    );
  };

  const toggleExpanded = (id: string) => {
    setExpandedStepId((current) => (current === id ? null : id));
  };

  const handleSave = () => {
    for (const step of steps) {
      for (const [toolId, processTimeSec] of Object.entries(step.toolProcessTimes)) {
        dispatch(
          updateInstance({
            id: toolId,
            changes: { processWaitSec: processTimeSec },
            skipHistory: true,
          })
        );
      }
    }
    const stockerProcessTypeById = new Map(
      steps.flatMap((step) =>
        step.stockerIds?.[0] ? [[step.stockerIds[0], step.processType] as const] : []
      )
    );
    const layoutStockerUpdates: Parameters<typeof updateElements>[0] = [];
    for (const stocker of stockers) {
      const processType = stockerProcessTypeById.get(stocker.id);
      const processWaitSec = stockerWaitSec[stocker.id] ?? stocker.processWaitSec;
      if (stocker.source === 'equipment') {
        dispatch(
          updateInstance({
            id: stocker.id,
            changes: {
              processTypes: processType ? [processType] : [],
              processWaitSec,
              ...(processType ? { equipmentType: processType } : {}),
            },
            skipHistory: true,
          })
        );
      } else {
        layoutStockerUpdates.push({
          id: stocker.id,
          updates: {
            customProperties: {
              processTypes: processType ? [processType] : [],
              processType: processType ?? null,
              processWaitSec,
            },
          },
        });
      }
    }
    if (layoutStockerUpdates.length > 0) dispatch(updateElements(layoutStockerUpdates));
    dispatch(
      patchProjectMetadata({
        processFlow: {
          version: PROCESS_FLOW_VERSION,
          cycleCount,
          steps: steps.map((step) => ({
            id: step.id,
            processType: step.processType,
            stockerIds: step.stockerIds?.length ? step.stockerIds : null,
            toolProcessTimes: step.toolProcessTimes,
            toolActive: step.toolActive,
            toolOrderIds: step.toolOrderIds,
          })),
        },
      })
    );
    onClose();
  };

  const totalTools = steps.reduce(
    (sum, step) => sum + Object.keys(step.toolProcessTimes).length,
    0
  );
  const stepsWithTools = steps.filter(
    (step) => Object.keys(step.toolProcessTimes).length > 0
  ).length;

  return createPortal(
    <div
      className="fixed inset-0 z-90 flex items-center justify-center bg-gray-950/45 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Configure process flow"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Configure process flow</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Drag the processes into the order a FOUP visits them, drag tools within each
              process to set intrabay visit order, then set dedicated stockers and process
              times. Wait and process times are wall / real seconds.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            Close
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 scrollbar-thin">
          {/* <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
            <div className="min-w-0">
              <label
                htmlFor="process-flow-cycle-count"
                className="text-xs font-semibold text-slate-700"
              >
                Number of cycles
              </label>
              <p className="mt-0.5 text-[11px] leading-snug text-slate-500">
                How many times a FOUP repeats this process route.
              </p>
            </div>
            <input
              id="process-flow-cycle-count"
              type="number"
              min={1}
              step={1}
              value={cycleCount}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value) || value < 1) return;
                setCycleCount(Math.floor(value));
              }}
              className="w-20 shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-right text-sm tabular-nums text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div> */}

          <div className="space-y-2">
            <EndpointCard
              label="Start"
              tone="emerald"
              detail={
                hasEntryGate
                  ? 'FOUPs are released from the entry gate.'
                  : 'No entry gate marked yet — tag a track node as Entry in the Properties panel.'
              }
              warn={!hasEntryGate}
            />

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={() => setExpandedStepId(null)}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={steps.map((step) => step.id)}
                strategy={verticalListSortingStrategy}
              >
                {steps.length === 0 ? (
                  <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11px] text-amber-800">
                    No equipment on this layout yet. Identify equipment and set its process type to
                    add steps here.
                  </p>
                ) : (
                  steps.map((step, index) => {
                    const stepTools = toolsInStepOrder(
                      step,
                      toolsByProcessType.get(step.processType) ?? []
                    );
                    const expanded = expandedStepId === step.id;
                    const selectedStockerId = step.stockerIds?.[0] ?? '';
                    const selectedStocker = stockers.find(
                      (stocker) => stocker.id === selectedStockerId
                    );
                    const waitSec = selectedStockerId
                      ? (stockerWaitSec[selectedStockerId] ?? DEFAULT_STOCKER_WAIT_SEC)
                      : DEFAULT_STOCKER_WAIT_SEC;
                    const waitLabel = Number.isInteger(waitSec)
                      ? String(waitSec)
                      : waitSec.toFixed(2);
                    return (
                      <SortableStep key={step.id} id={step.id}>
                        {(dragHandle) => (
                          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                            <section className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                              <div className="flex min-h-10 shrink-0 items-center gap-2 bg-slate-50 px-3 py-2">
                                {dragHandle}
                                <button
                                  type="button"
                                  onClick={() => toggleExpanded(step.id)}
                                  aria-expanded={expanded}
                                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                >
                                  <span className="w-3 shrink-0 text-[10px] text-slate-400">
                                    {expanded ? '▼' : '▶'}
                                  </span>
                                  <span
                                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                                      stepTools.length === 0
                                        ? 'bg-slate-300 text-slate-600'
                                        : 'bg-slate-800 text-white'
                                    }`}
                                  >
                                    {index + 1}
                                  </span>
                                  <span className="truncate text-sm font-semibold text-slate-900">
                                    {equipmentProcessTypeLabel(step.processType)}
                                  </span>
                                  {stepTools.length === 0 ? (
                                    <span className="shrink-0 text-[11px] text-amber-700">
                                      no tools
                                    </span>
                                  ) : (
                                    <span className="shrink-0 text-[11px] text-slate-500">
                                      {stepTools.length} {stepTools.length === 1 ? 'tool' : 'tools'}
                                    </span>
                                  )}
                                </button>
                              </div>

                              {expanded && (
                                <div className="space-y-3 px-3 py-3">
                                  <div>
                                    <div className="mb-1.5 flex items-center justify-between">
                                      <span className="text-[11px] font-semibold text-slate-600">
                                        Process time per tool (s)
                                      </span>
                                      {stepTools.length > 1 ? (
                                        <span className="text-[10px] text-slate-400">
                                          Drag tools to set visit order
                                        </span>
                                      ) : null}
                                    </div>

                                    {stepTools.length === 0 ? (
                                      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                                        No equipment tagged as {step.processType} in this layout, so
                                        this step is skipped. Set the process type on equipment in
                                        the Properties panel to use it.
                                      </p>
                                    ) : (
                                      <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={(event) => handleToolDragEnd(step.id, event)}
                                      >
                                        <SortableContext
                                          items={step.toolOrderIds}
                                          strategy={verticalListSortingStrategy}
                                        >
                                          <ul className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200">
                                            {stepTools.map((tool) => {
                                              const isActive = step.toolActive[tool.id] ?? true;
                                              return (
                                                <SortableToolRow key={tool.id} id={tool.id}>
                                                  {(dragHandle) => (
                                                    <div className="flex items-center justify-between gap-3 px-3 py-1.5">
                                                      <div className="flex min-w-0 flex-1 items-center gap-2">
                                                        {dragHandle}
                                                        <ToolActiveToggle
                                                          active={isActive}
                                                          toolName={tool.name}
                                                          onToggle={() =>
                                                            updateStep(step.id, {
                                                              toolActive: {
                                                                ...step.toolActive,
                                                                [tool.id]: !isActive,
                                                              },
                                                            })
                                                          }
                                                        />
                                                        <span className="truncate text-xs text-slate-700">
                                                          {tool.name}
                                                        </span>
                                                      </div>
                                                      <input
                                                        type="number"
                                                        min={0}
                                                        step={1}
                                                        aria-label={`Process time in wall seconds for ${tool.name}`}
                                                        value={
                                                          step.toolProcessTimes[tool.id] ??
                                                          DEFAULT_PROCESS_FLOW_TIME_SEC
                                                        }
                                                        onChange={(event) => {
                                                          const seconds = Number(event.target.value);
                                                          if (!Number.isFinite(seconds) || seconds < 0)
                                                            return;
                                                          updateStep(step.id, {
                                                            toolProcessTimes: {
                                                              ...step.toolProcessTimes,
                                                              [tool.id]: seconds,
                                                            },
                                                          });
                                                        }}
                                                        className="w-20 shrink-0 rounded border border-slate-300 px-2 py-1 text-right text-xs tabular-nums text-slate-800 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                                      />
                                                    </div>
                                                  )}
                                                </SortableToolRow>
                                              );
                                            })}
                                          </ul>
                                        </SortableContext>
                                      </DndContext>
                                    )}
                                  </div>
                                </div>
                              )}
                            </section>

                            <div
                              className="flex min-h-10 items-center justify-center text-sm text-slate-400"
                              aria-hidden="true"
                            >
                              →
                            </div>

                            <aside className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                              <button
                                type="button"
                                onClick={() => toggleExpanded(step.id)}
                                aria-expanded={expanded}
                                className="flex min-h-10 shrink-0 items-center gap-2 bg-slate-50 px-3 py-2 text-left"
                              >
                                <span className="w-3 shrink-0 text-[10px] text-slate-400">
                                  {expanded ? '▼' : '▶'}
                                </span>
                                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
                                  Stocker:{' '}
                                  <span className="font-medium text-slate-700">
                                    {selectedStocker?.name ?? 'None'}
                                  </span>
                                </span>
                                <span className="shrink-0 text-[11px] text-slate-500">
                                  Wait time: {selectedStockerId ? `${waitLabel} s` : '—'}
                                </span>
                              </button>

                              {expanded && (
                                <div className="grid grid-cols-2 items-end gap-2 px-3 py-3">
                                  <div className="min-w-0">
                                    <label
                                      htmlFor={`process-stocker-${step.id}`}
                                      className="mb-1 block text-[11px] font-semibold text-slate-600"
                                    >
                                      Dedicated stocker
                                    </label>
                                    <select
                                      id={`process-stocker-${step.id}`}
                                      value={selectedStockerId}
                                      onChange={(event) =>
                                        assignStocker(step.id, event.target.value || null)
                                      }
                                      className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    >
                                      <option value="">None</option>
                                      {stockers.map((stocker) => (
                                        <option key={stocker.id} value={stocker.id}>
                                          {stocker.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="min-w-0">
                                    <label
                                      htmlFor={`stocker-wait-${step.id}`}
                                      className="mb-1 block text-[11px] font-semibold text-slate-600"
                                    >
                                      Wait time (s)
                                    </label>
                                    <input
                                      id={`stocker-wait-${step.id}`}
                                      type="number"
                                      min={0}
                                      step={1}
                                      disabled={!selectedStockerId}
                                      value={selectedStockerId ? waitSec : ''}
                                      onChange={(event) => {
                                        const seconds = Number(event.target.value);
                                        if (
                                          !selectedStockerId ||
                                          !Number.isFinite(seconds) ||
                                          seconds < 0
                                        ) {
                                          return;
                                        }
                                        setStockerWaitSec((current) => ({
                                          ...current,
                                          [selectedStockerId]: Math.round(seconds * 100) / 100,
                                        }));
                                      }}
                                      aria-label={`Stocker wait time for ${equipmentProcessTypeLabel(step.processType)}`}
                                      className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-right text-xs tabular-nums text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                                    />
                                  </div>
                                </div>
                              )}
                            </aside>
                          </div>
                        )}
                      </SortableStep>
                    );
                  })
                )}
              </SortableContext>
            </DndContext>

            <EndpointCard
              label="End"
              tone="slate"
              detail={
                hasExitGate
                  ? 'Finished FOUPs leave through the exit gate.'
                  : 'No exit gate marked yet — tag a track node as Exit in the Properties panel.'
              }
              warn={!hasExitGate}
            />
          </div>
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-3">
          <span className="text-[11px] text-slate-500">
            {stepsWithTools} of {steps.length} processes staffed · {totalTools}{' '}
            {totalTools === 1 ? 'tool' : 'tools'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-lg bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
            >
              Save process flow
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
};

const SortableStep: React.FC<{
  id: string;
  children: (dragHandle: React.ReactNode) => React.ReactNode;
}> = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const dragHandle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      title="Drag to reorder"
      aria-label="Drag to reorder step"
      className="flex h-6 w-4 shrink-0 cursor-grab touch-none items-center justify-center rounded text-xs leading-none text-slate-400 hover:bg-slate-200 hover:text-slate-700 active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      ⠿
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'relative z-10 opacity-80' : undefined}
    >
      {children(dragHandle)}
    </div>
  );
};

const SortableToolRow: React.FC<{
  id: string;
  children: (dragHandle: React.ReactNode) => React.ReactNode;
}> = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const dragHandle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      title="Drag to reorder tool visit"
      aria-label="Drag to reorder tool visit"
      className="flex h-5 w-3.5 shrink-0 cursor-grab touch-none items-center justify-center rounded text-[10px] leading-none text-slate-400 hover:bg-slate-200 hover:text-slate-700 active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      ⠿
    </button>
  );

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'relative z-10 bg-white opacity-80' : undefined}
    >
      {children(dragHandle)}
    </li>
  );
};

const EndpointCard: React.FC<{
  label: string;
  detail: string;
  tone: 'emerald' | 'slate';
  warn?: boolean;
}> = ({ label, detail, tone, warn }) => (
  <div
    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
      tone === 'emerald' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-300 bg-slate-100'
    }`}
  >
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wider text-white ${
        tone === 'emerald' ? 'bg-emerald-600' : 'bg-slate-700'
      }`}
    >
      {label.toUpperCase()}
    </span>
    <span className={`text-[11px] ${warn ? 'text-amber-800' : 'text-slate-600'}`}>{detail}</span>
  </div>
);

const ToolActiveToggle: React.FC<{
  active: boolean;
  toolName: string;
  onToggle: () => void;
}> = ({ active, toolName, onToggle }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={active}
    aria-label={`${toolName} ${active ? 'active' : 'inactive'}`}
    title={active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
    onClick={onToggle}
    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[11px] font-bold leading-none transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${
      active
        ? 'border-emerald-500 bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
        : 'border-red-400 bg-red-50 text-red-500 hover:bg-red-100'
    }`}
  >
    {active ? '✓' : '✕'}
  </button>
);

export default ProcessFlowConfigModal;
